
interface IndustryCardProps {
  name: string;
  tagline: string;
  description: string;
  image: string;
  stats: { label: string; value: string }[];
  challenges: string[];
  solutions: string[];
  icon: string;
  reversed?: boolean;
}

export default function IndustryCard({
  name,
  tagline,
  description,
  image,
  stats,
  challenges,
  solutions,
  icon,
  reversed = false,
}: IndustryCardProps) {
  return (
    <div
      className={`
        grid lg:grid-cols-2 gap-0 bg-white rounded-lg overflow-hidden
        shadow-sm border border-gray-100 hover:shadow-md transition-shadow
        ${reversed ? 'lg:[direction:rtl]' : ''}
      `}
    >
      {/* Image Side */}
      <div className="relative w-full h-[320px] lg:h-auto overflow-hidden">
        <img src={image} alt={name} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
        <div className="absolute bottom-4 left-4 right-4 flex gap-3">
          {stats.map((stat, i) => (
            <div
              key={i}
              className="bg-white/95 backdrop-blur-sm rounded-md px-3 py-2 flex-1 text-center"
            >
              <div className="text-lg font-bold text-emerald-800">{stat.value}</div>
              <div className="text-[11px] text-gray-500 leading-tight">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Content Side */}
      <div
        className={`
          p-8 lg:p-10 flex flex-col justify-center
          ${reversed ? 'lg:[direction:ltr]' : ''}
        `}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 flex items-center justify-center bg-emerald-100 rounded-lg">
            <i className={`${icon} text-xl text-emerald-700`}></i>
          </div>
          <span className="text-xs font-bold text-emerald-700 tracking-widest uppercase">
            {tagline}
          </span>
        </div>

        <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-3">{name}</h3>
        <p className="text-gray-600 leading-relaxed mb-6">{description}</p>

        <div className="grid grid-cols-2 gap-6 mb-6">
          <div>
            <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-error-warning-line text-red-500 text-sm"></i>
              </div>
              Challenges
            </h4>
            <ul className="space-y-1.5">
              {challenges.map((c, i) => (
                <li
                  key={i}
                  className="text-sm text-gray-600 flex items-start gap-1.5"
                >
                  <span className="text-red-400 mt-0.5 shrink-0">&bull;</span>
                  {c}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-bold text-gray-900 mb-2 flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-check-line text-emerald-600 text-sm"></i>
              </div>
              Our Solutions
            </h4>
            <ul className="space-y-1.5">
              {solutions.map((s, i) => (
                <li
                  key={i}
                  className="text-sm text-gray-600 flex items-start gap-1.5"
                >
                  <span className="text-emerald-500 mt-0.5 shrink-0">&bull;</span>
                  {s}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <a
          href="/#contact"
          className="inline-flex items-center gap-2 text-emerald-700 font-bold text-sm hover:text-emerald-900 transition-colors cursor-pointer group"
        >
          Discuss Your {name} Needs
          <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
        </a>
      </div>
    </div>
  );
}
